import { useQuery } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { type ReactNode, useEffect, useRef } from 'react';

import { ApiError } from '../../lib/api';
import { t } from '../../lib/i18n';
import { fetchSessionUser } from '../clients/api';

/**
 * Nothing behind the shell renders without an ADMIN session. No session (401)
 * or a customer's one (role without `admin`) goes to /login, carrying the page
 * that was asked for. Shares the `session-user` query with useSessionUser, so
 * the shell's avatar costs no second request.
 *
 * Why not `<Navigate>`: in this router version it navigates whenever its props
 * object is new — i.e. on every render — and the guard re-renders while the
 * navigation is pending, so it looped until the tab ran out of memory. The
 * redirect fires ONCE per decision instead.
 */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const here = useRouterState({
    select: (state) => `${state.location.pathname}${state.location.searchStr}`,
  });
  const navigate = useNavigate();
  const session = useQuery({
    queryKey: ['session-user'],
    queryFn: fetchSessionUser,
    staleTime: 5 * 60_000,
  });

  const unauthenticated =
    session.error instanceof ApiError &&
    (session.error.status === 401 || session.error.status === 403);
  const notAdmin = Boolean(session.data) && !(session.data?.role ?? []).includes('admin');
  const redirect = unauthenticated ? 'sign-in' : notAdmin ? 'not-admin' : null;

  const firedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!redirect || firedFor.current === redirect) return;
    firedFor.current = redirect;
    void navigate({
      to: '/login',
      search: redirect === 'not-admin' ? { next: here, reason: 'not-admin' } : { next: here },
      replace: true,
    });
  }, [redirect, here, navigate]);

  if (session.isLoading || redirect) {
    return (
      <div
        role="status"
        className="flex min-h-screen items-center justify-center text-sm text-cf-ink-subtle"
      >
        {t('admin.login.checking')}
      </div>
    );
  }

  // A 5xx / network error falls through: the pages render their own error
  // states, and bouncing an admin to /login over an outage would be wrong.
  return <>{children}</>;
}
